import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { SportProvider } from './context/SportContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import AthleteListPage from './pages/AthleteListPage';
import AthleteDetailPage from './pages/AthleteDetailPage';
import AthleteSeasonAnalyticsPage from './pages/AthleteSeasonAnalyticsPage';
import TeamSeasonAnalyticsPage from './pages/TeamSeasonAnalyticsPage';
import ExecutiveSeasonAnalyticsPage from './pages/ExecutiveSeasonAnalyticsPage';
import TeamListPage from './pages/TeamListPage';
import TeamDetailPage from './pages/TeamDetailPage';
import PlannerCalendarPage from './pages/PlannerCalendarPage';
import PlannerDayPage from './pages/PlannerDayPage';
import SessionDetailPage from './pages/SessionDetailPage';
import DaySnapshotPage from './pages/DaySnapshotPage';
import StaffWellnessPage from './pages/StaffWellnessPage';
import AthleteWellnessPage from './pages/AthleteWellnessPage';
import StaffVBTPage from './pages/StaffVBTPage';
import VBTTestDetailPage from './pages/VBTTestDetailPage';
import AthleteVBTProfilePage from './pages/AthleteVBTProfilePage';
import AthleteVBTPage from './pages/AthleteVBTPage';
import GroupVBTTestPage from './pages/GroupVBTTestPage';
import StaffRTPPage from './pages/StaffRTPPage';
import StaffGPSPage from './pages/StaffGPSPage';
import StaffReportsPage from './pages/StaffReportsPage';
import RTPAnalyticsPage from './pages/RTPAnalyticsPage';
import TrainingDistributionPage from './pages/TrainingDistributionPage';
import AthleteWeightPage from './pages/AthleteWeightPage';
import AthleteGPSPage from './pages/AthleteGPSPage';
import AthleteSchedulePage from './pages/AthleteSchedulePage';
import StaffJumpPage from './pages/StaffJumpPage';
import GroupJumpTestPage from './pages/GroupJumpTestPage';
import JumpSessionDetailPage from './pages/JumpSessionDetailPage';
import AthleteJumpProfilePage from './pages/AthleteJumpProfilePage';
import AthleteJumpPage from './pages/AthleteJumpPage';
import StaffAerobicPage from './pages/StaffAerobicPage';
import GroupAerobicTestPage from './pages/GroupAerobicTestPage';
import AerobicSessionDetailPage from './pages/AerobicSessionDetailPage';
import AthleteAerobicProfilePage from './pages/AthleteAerobicProfilePage';
import AthleteAerobicPage from './pages/AthleteAerobicPage';
import AdminPage from './pages/AdminPage';
import TrendChartsPage from './pages/TrendChartsPage';
import TeamPerformanceDashboardPage from './pages/TeamPerformanceDashboardPage';
import SeasonTransitionPage from './pages/SeasonTransitionPage';
import UserManagementPage from './pages/UserManagementPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import SuperAdminPage from './pages/SuperAdminPage';
import IntegrityDashboardPage from './pages/superadmin/IntegrityDashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SettingsPage from './pages/SettingsPage';
import StaffNotificationsPage from './pages/staff/StaffNotificationsPage';
import ExportPage from './pages/staff/ExportPage';

// Prescription Engine Pages
import PrescriptionHomePage from './pages/staff/prescriptions/PrescriptionHomePage';
import PrescriptionBuilderPage from './pages/staff/prescriptions/PrescriptionBuilderPage';
import PrescriptionDetailPage from './pages/staff/prescriptions/PrescriptionDetailPage';
import PrescriptionLibraryPage from './pages/staff/prescriptions/PrescriptionLibraryPage';
import PrescriptionTemplatesPage from './pages/staff/prescriptions/PrescriptionTemplatesPage';
import AthletePrescriptionsPage from './pages/athlete/prescriptions/AthletePrescriptionsPage';

// Phase 2.1B — Nutrition Module
import StaffNutritionIndexPage from './pages/staff/StaffNutritionIndexPage';
import AthleteNutritionDashboardPage from './pages/staff/AthleteNutritionDashboardPage';
import TeamNutritionDashboardPage from './pages/staff/TeamNutritionDashboardPage';
import ExecutiveNutritionDashboardPage from './pages/staff/ExecutiveNutritionDashboardPage';
import AthleteNutritionHomePage from './pages/athlete/AthleteNutritionHomePage';

// Layouts
import StaffLayout from './layouts/StaffLayout';
import AthleteLayout from './layouts/AthleteLayout';

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user, getHomeRoute } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={getHomeRoute()} replace />;
  }

  return children;
};

// Staff Route wrapper
const StaffRoute = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['ADMIN', 'SNC', 'PHYSIO', 'COACH', 'NUTRITIONIST']}>
      <StaffLayout>{children}</StaffLayout>
    </ProtectedRoute>
  );
};

// Athlete Route wrapper
const AthleteRoute = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['ATHLETE']}>
      <AthleteLayout>{children}</AthleteLayout>
    </ProtectedRoute>
  );
};

// Public Route wrapper (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading, getHomeRoute } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={getHomeRoute()} replace />;
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated, getHomeRoute } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Staff routes */}
      <Route
        path="/staff/roster"
        element={
          <StaffRoute>
            <AthleteListPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/roster/teams"
        element={
          <StaffRoute>
            <TeamListPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/roster/teams/:teamId"
        element={
          <StaffRoute>
            <TeamDetailPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/roster/:athleteId"
        element={
          <StaffRoute>
            <AthleteDetailPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/roster/:athleteId/season"
        element={
          <StaffRoute>
            <AthleteSeasonAnalyticsPage />
          </StaffRoute>
        }
      />
      <Route
        path="/athlete/season"
        element={
          <AthleteRoute>
            <AthleteSeasonAnalyticsPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/staff/team/:teamId/season"
        element={
          <StaffRoute>
            <TeamSeasonAnalyticsPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/executive/season"
        element={
          <StaffRoute>
            <ExecutiveSeasonAnalyticsPage />
          </StaffRoute>
        }
      />

      {/* Planner routes */}
      <Route
        path="/staff/planner"
        element={
          <StaffRoute>
            <PlannerCalendarPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/planner/day/:date"
        element={
          <StaffRoute>
            <PlannerDayPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/planner/session/:sessionId"
        element={
          <StaffRoute>
            <SessionDetailPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/planner/snapshot/:date"
        element={
          <StaffRoute>
            <DaySnapshotPage />
          </StaffRoute>
        }
      />

      {/* Wellness routes - Staff */}
      <Route
        path="/staff/wellness"
        element={
          <StaffRoute>
            <StaffWellnessPage />
          </StaffRoute>
        }
      />

      {/* VBT routes - Staff */}
      <Route
        path="/staff/vbt"
        element={
          <StaffRoute>
            <StaffVBTPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/vbt/group-test"
        element={
          <StaffRoute>
            <GroupVBTTestPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/vbt/tests/:testId"
        element={
          <StaffRoute>
            <VBTTestDetailPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/vbt/athlete/:athleteId"
        element={
          <StaffRoute>
            <AthleteVBTProfilePage />
          </StaffRoute>
        }
      />

      {/* RTP routes - Staff */}
      <Route
        path="/staff/rtp"
        element={
          <StaffRoute>
            <StaffRTPPage />
          </StaffRoute>
        }
      />

      {/* Jump Testing routes - Staff */}
      <Route
        path="/staff/jump"
        element={
          <StaffRoute>
            <StaffJumpPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/jump/group-test"
        element={
          <StaffRoute>
            <GroupJumpTestPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/jump/session/:sessionId"
        element={
          <StaffRoute>
            <JumpSessionDetailPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/jump/athlete/:athleteId"
        element={
          <StaffRoute>
            <AthleteJumpProfilePage />
          </StaffRoute>
        }
      />

      {/* Aerobic Testing routes - Staff */}
      <Route
        path="/staff/aerobic"
        element={
          <StaffRoute>
            <StaffAerobicPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/aerobic/group-test"
        element={
          <StaffRoute>
            <GroupAerobicTestPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/aerobic/session/:sessionId"
        element={
          <StaffRoute>
            <AerobicSessionDetailPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/aerobic/athlete/:athleteId"
        element={
          <StaffRoute>
            <AthleteAerobicProfilePage />
          </StaffRoute>
        }
      />

      {/* GPS routes - Staff */}
      <Route
        path="/staff/gps"
        element={
          <StaffRoute>
            <StaffGPSPage />
          </StaffRoute>
        }
      />

      {/* Reports routes - Staff */}
      <Route
        path="/staff/reports"
        element={
          <StaffRoute>
            <StaffReportsPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/reports/rtp-analytics"
        element={
          <StaffRoute>
            <RTPAnalyticsPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/reports/training-distribution"
        element={
          <StaffRoute>
            <TrainingDistributionPage />
          </StaffRoute>
        }
      />

      {/* Trend Charts - Staff */}
      <Route
        path="/staff/trends"
        element={
          <StaffRoute>
            <TrendChartsPage />
          </StaffRoute>
        }
      />

      {/* Team Performance Dashboard - Staff */}
      <Route
        path="/staff/dashboard"
        element={
          <StaffRoute>
            <TeamPerformanceDashboardPage />
          </StaffRoute>
        }
      />

      {/* Prescription Engine routes - Staff */}
      <Route
        path="/staff/prescriptions"
        element={
          <StaffRoute>
            <PrescriptionHomePage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/prescriptions/builder"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'SNC', 'PHYSIO']}>
            <StaffLayout><PrescriptionBuilderPage /></StaffLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/prescriptions/library"
        element={
          <StaffRoute>
            <PrescriptionLibraryPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/prescriptions/templates"
        element={
          <StaffRoute>
            <PrescriptionTemplatesPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/prescriptions/:prescriptionId"
        element={
          <StaffRoute>
            <PrescriptionDetailPage />
          </StaffRoute>
        }
      />

      {/* Admin routes - ADMIN only */}
      <Route
        path="/staff/notifications"
        element={
          <StaffRoute>
            <StaffNotificationsPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/export"
        element={
          <StaffRoute>
            <ExportPage />
          </StaffRoute>
        }
      />
      <Route
        path="/staff/admin"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <StaffLayout><AdminPage /></StaffLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/admin/season"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <StaffLayout><SeasonTransitionPage /></StaffLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/admin/users"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <StaffLayout><UserManagementPage /></StaffLayout>
          </ProtectedRoute>
        }
      />

      {/* Phase 2.1B — Nutrition (staff) */}
      <Route
        path="/staff/nutrition"
        element={<StaffRoute><StaffNutritionIndexPage /></StaffRoute>}
      />
      <Route
        path="/staff/nutrition/executive"
        element={<StaffRoute><ExecutiveNutritionDashboardPage /></StaffRoute>}
      />
      <Route
        path="/staff/nutrition/teams/:teamId"
        element={<StaffRoute><TeamNutritionDashboardPage /></StaffRoute>}
      />
      <Route
        path="/staff/nutrition/athletes/:athleteId"
        element={<StaffRoute><AthleteNutritionDashboardPage /></StaffRoute>}
      />

      {/* Phase 2.1B — Nutrition (athlete) */}
      <Route
        path="/athlete/nutrition"
        element={<AthleteRoute><AthleteNutritionHomePage /></AthleteRoute>}
      />

      {/* Public invite acceptance */}
      <Route
        path="/accept-invite"
        element={<AcceptInvitePage />}
      />

      {/* Public password reset */}
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />

      {/* SuperAdmin route */}
      <Route
        path="/superadmin"
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN']}>
            <SuperAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin/integrity"
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN']}>
            <StaffLayout><IntegrityDashboardPage /></StaffLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings (all logged-in users) */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'SNC', 'PHYSIO', 'COACH', 'ATHLETE']}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Athlete routes */}
      <Route
        path="/athlete/wellness"
        element={
          <AthleteRoute>
            <AthleteWellnessPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/weight"
        element={
          <AthleteRoute>
            <AthleteWeightPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/vbt"
        element={
          <AthleteRoute>
            <AthleteVBTPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/load"
        element={
          <AthleteRoute>
            <AthleteGPSPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/schedule"
        element={
          <AthleteRoute>
            <AthleteSchedulePage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/jump"
        element={
          <AthleteRoute>
            <AthleteJumpPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/aerobic"
        element={
          <AthleteRoute>
            <AthleteAerobicPage />
          </AthleteRoute>
        }
      />
      <Route
        path="/athlete/prescriptions"
        element={
          <AthleteRoute>
            <AthletePrescriptionsPage />
          </AthleteRoute>
        }
      />

      {/* Redirect root to appropriate page */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to={getHomeRoute()} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Catch all - redirect to home or login */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate to={getHomeRoute()} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <SportProvider>
            <AppRoutes />
            <Toaster 
              position="top-right" 
              toastOptions={{
                style: {
                  background: 'hsl(240 10% 6%)',
                  border: '1px solid hsl(240 3.7% 15.9%)',
                  color: 'hsl(0 0% 98%)',
                },
              }}
            />
          </SportProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
